import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { ConfigService } from '@services/config.service';
import { FileUploadService } from '@services/fileUpload.service';
import { EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';

import { setup } from './setup';
import { schema } from './setup/schema';
import { docFromJsonString, jsonStringFromDoc, textFromDoc } from './util';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'prosemirror',
  templateUrl: './prosemirror.component.html',
  styleUrls: ['./prosemirror.component.scss'],
})
export class ProsemirrorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('container', { static: true }) container: ElementRef;
  @ViewChild('image', { static: true }) imageInput: ElementRef;

  @Input() placeholder = 'Type something ...';
  @Input() characterLimit = -1;
  @Input() minHeight = 0;
  @Input() simpleMenu = false;
  @Input() twoliner = false;
  @Output() contentChange = new EventEmitter<string>();
  @Output() raw = new EventEmitter<string>();
  @Output() valid = new EventEmitter<boolean>();
  @Input() animate = false; //if enabled applies border and animates opacity

  view: EditorView;
  state: EditorState;
  _content: string;

  charcount = -1;
  showCharCount = false;

  subs = new Subscription();
  upload$ = new Subject<FileList>();
  showCharcount$ = new Subject<void>();

  constructor(
    private modal: NgbModal,
    private upload: FileUploadService,
    private config: ConfigService,
    private assetsPipe: AssetsPipe,
  ) {}

  ngOnInit() {
    this.subs.add(
      this.showCharcount$
        .pipe(
          filter(() => this.characterLimit > 0),
          tap(() => (this.showCharCount = true)),
          debounceTime(2000),
          filter(() => this.isValid()),
          tap(() => (this.showCharCount = false)),
        )
        .subscribe(),
    );

    this.subs.add(
      fromEvent(document.body, 'scroll').subscribe(() => this.scroll()),
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  @Input() set content(content: string) {
    this._content = content;
  }

  @Input() set initialText(text: string) {
    if (this.state) {
      this.state = this.state.apply(
        this.state.tr.insertText(text, 0, this.state.doc.content.size - 1),
      );
      this.view.updateState(this.state);
      setTimeout(() => this.emit(), 10);
    }
  }

  createView() {
    this.state = EditorState.create({
      doc: docFromJsonString(schema, this._content),
      plugins: setup(schema, {
        simpleMenu: this.simpleMenu,
        modal: this.modal,
        uploader: {
          select: () => this.imageInput.nativeElement.click(),
          upload: this.upload$.pipe(
            map(files =>
              this.upload
                .upload('/upload', files.item(0))
                .pipe(map(response => this.assetsPipe.transform(response.key))),
            ),
          ),
        },
      }),
    });

    setTimeout(() => (this.charcount = textFromDoc(this.state.doc).length), 1);
    this.view = new EditorView(this.container.nativeElement, {
      state: this.state,
      dispatchTransaction: tr => {
        this.view.updateState(this.view.state.apply(tr));
        this.emit();
      },
    });
  }

  ngAfterViewInit() {
    if (!this.view) {
      this.createView();
    }
  }

  isValid() {
    if (this.characterLimit > 0) {
      return this.charcount < this.characterLimit;
    } else {
      return true;
    }
  }

  emit() {
    this.contentChange.emit(jsonStringFromDoc(this.view.state.doc));

    const text = textFromDoc(this.view.state.doc);
    const charcount = text.length;

    if (charcount !== this.charcount) {
      if (this.characterLimit > 0) {
        this.showCharcount$.next();
      }

      this.charcount = charcount;
    } else if (!this.isValid()) {
      this.showCharcount$.next();
    }

    this.raw.emit(text);
    this.valid.emit(this.isValid());
  }

  scroll() {
    const elTop = this.container.nativeElement.getBoundingClientRect().top;
    const offset = window.innerHeight - elTop;

    if (offset < 128) {
      this.showCharCount = false;
    }
  }
}
